import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import Results from './Results';

// --- CONSTANTS ---
const API_URL = 'https://careerquest.scaleo.hr/api/v1';

// --- NAVIGATION BAR COMPONENT ---
function NavigationBar({ showDashboardButton, onViewDashboard }) {
  return (
    <nav className="main-navbar">
      <div className="navbar-content">
        <div className="navbar-brand">
          <img 
            src="/logo career quest.png" 
            alt="Career Quest Logo" 
            className="navbar-logo"
          />
        </div>
        {showDashboardButton && (
          <button onClick={onViewDashboard} className="navbar-action-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Voir le tableau de bord
          </button>
        )}
      </div>
    </nav>
  );
}

function App() {
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [resultId, setResultId] = useState('');
  const [message, setMessage] = useState('');
  const [userId, setUserId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userFullName, setUserFullName] = useState('');
  const [showLoginForm, setShowLoginForm] = useState(true);
  const [viewMode, setViewMode] = useState('quiz'); // 'quiz', 'results', 'dashboard'
  const [showInstructions, setShowInstructions] = useState(false);
  const [canGoBack, setCanGoBack] = useState(true); // Track if user can go back one question

  // --- FETCH QUESTIONS ---
  useEffect(() => {
    axios.get(`${API_URL}/questions`)
      .then(res => {
        setQuestions(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching questions", err);
        setLoading(false);
      });
  }, []);

  // --- HANDLERS ---
  const handleLogin = (e) => {
    e.preventDefault();
    if (userEmail && userFullName) {
      // Generate userId from email
      const generatedUserId = 'user_' + userEmail.split('@')[0] + '_' + Date.now();
      setUserId(generatedUserId);
      setShowLoginForm(false);
      setShowInstructions(true);
    }
  };

  const handleAnswer = (option) => {
    const currentQ = questions[currentQuestionIndex];
    const newAnswers = { ...answers, [currentQ.id]: option };
    setAnswers(newAnswers);
    setCanGoBack(true); // Reset back ability when answering

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      submitTest(newAnswers);
    }
  };

  const submitTest = async (finalAnswers) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/results/submit`, {
        answers: finalAnswers,
        userId: userId,
        email: userEmail,
        fullName: userFullName
      });
      setResults(res.data.results);
      setResultId(res.data.result_id);
      setMessage(res.data.message);
      setShowResults(true);
      setLoading(false);
    } catch (error) {
      console.error("Error submitting", error);
      alert("Erreur lors de la soumission. Veuillez réessayer.");
      setLoading(false);
    }
  };

  // --- RENDERERS ---
  if (loading && viewMode === 'quiz') {
    return (
      <div className="loading-screen">
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            border: '4px solid #E5E7EB', 
            borderTop: '4px solid #4EAEF5',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <h2 style={{ color: '#4EAEF5', fontWeight: '600' }}>Chargement du test...</h2>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // View Dashboard
  if (viewMode === 'dashboard') {
    return (
      <>
        <NavigationBar showDashboardButton={false} />
        <div style={{ marginTop: '80px' }}>
          <Results />
        </div>
      </>
    );
  }

  if (showResults) {
    return <ResultsView results={results} resultId={resultId} message={message} onViewDashboard={() => setViewMode('dashboard')} />;
  }

  // Show Login Form
  if (showLoginForm && questions.length > 0) {
    return (
      <div className="app-container">
        <NavigationBar showDashboardButton={false} />

        <main style={{ padding: '32px 24px', maxWidth: '600px', margin: '0 auto', width: '100%', marginTop: '120px' }}>
          <div style={{
            background: 'white',
            border: '2px solid #e0e0e0',
            padding: '48px',
            borderRadius: '16px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
          }}>
            <h2 style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: '#1F2937', 
              marginBottom: '12px',
              textAlign: 'center'
            }}>
              Bienvenue sur Career Quest
            </h2>
            <p style={{ 
              textAlign: 'center', 
              color: '#6B7280', 
              marginBottom: '32px',
              fontSize: '15px'
            }}>
              Veuillez entrer vos informations pour commencer le test
            </p>

            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Nom complet *
                </label>
                <input
                  type="text"
                  value={userFullName}
                  onChange={(e) => setUserFullName(e.target.value)}
                  required
                  placeholder="Ex: Jean Dupont"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '15px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#42a5f5'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Adresse e-mail *
                </label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  required
                  placeholder="exemple@email.com"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '15px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#42a5f5'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              <button
                type="submit"
                style={{
                  width: '100%',
                  background: 'linear-gradient(90deg, #42a5f5 0%, #1e88e5 100%)',
                  color: 'white',
                  padding: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  borderRadius: '10px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(66, 165, 245, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(66, 165, 245, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(66, 165, 245, 0.3)';
                }}
              >
                Continuer
              </button>
            </form>
          </div>
        </main>
      </div>
    );
  }

  // Show Instructions Screen
  if (showInstructions && questions.length > 0) {
    return (
      <div className="app-container">
        <NavigationBar showDashboardButton={false} />

        <main style={{ padding: '32px 24px', maxWidth: '900px', margin: '0 auto', width: '100%', marginTop: '80px' }}>
          

          {/* Instructions Card */}
          <div style={{
            background: 'white',
            border: '2px solid #E5E7EB',
            padding: '40px',
            borderRadius: '16px',
            marginBottom: '40px',
            boxShadow: '0 6px 16px -1px rgba(0, 0, 0, 0.08)'
          }}>
            <h2 style={{ 
              fontSize: '26px', 
              fontWeight: '700', 
              color: '#1F2937', 
              marginBottom: '28px',
              textAlign: 'left',
              paddingBottom: '16px',
              borderBottom: '3px solid #4EAEF5'
            }}>
              Instructions
            </h2>
            <div style={{ color: '#4B5563', fontSize: '16px', lineHeight: '1.8', textAlign: 'left' }}>
              <p style={{ marginBottom: '24px', fontSize: '17px' }}>
                Le test <strong style={{ color: '#2563EB' }}>Career Quest</strong> a été conçu pour identifier les métiers et compétences qui correspondent le mieux à votre profil professionnel.
              </p>
              <p style={{ marginBottom: '16px', fontWeight: '600', color: '#1F2937' }}>
                Modalités du test :
              </p>
              <ul style={{ paddingLeft: '24px', marginBottom: '20px' }}>
                <li style={{ marginBottom: '12px' }}>
                  Le test comprend <strong>{questions.length} questions</strong> à choix multiples
                </li>
                <li style={{ marginBottom: '12px' }}>
                  Pour chaque question, sélectionnez l'option qui vous correspond le mieux
                </li>
                <li style={{ marginBottom: '12px' }}>
                  Il n'existe pas de bonnes ou mauvaises réponses
                </li>
                <li style={{ marginBottom: '12px' }}>
                  Vous pouvez revenir à la question précédente si nécessaire
                </li>
                <li style={{ marginBottom: '12px' }}>
                  Durée estimée : <strong>10 à 15 minutes</strong>
                </li>
              </ul>
              <div style={{ 
                background: '#F3F4F6', 
                padding: '16px 20px', 
                borderRadius: '8px',
                marginTop: '20px',
                borderLeft: '4px solid #4EAEF5'
              }}>
                <p style={{ margin: 0, fontWeight: '500' }}>
                  <strong>Recommandation :</strong> Répondez de manière spontanée et honnête. Votre première impression est généralement la plus révélatrice de vos préférences réelles.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(false)}
            style={{
              background: 'linear-gradient(90deg, #4EAEF5 0%, #2563EB 100%)',
              color: 'white',
              padding: '18px 64px',
              fontSize: '18px',
              fontWeight: '700',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(78, 174, 245, 0.3)',
              display: 'block',
              margin: '0 auto',
              width: '100%',
              maxWidth: '500px'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 20px rgba(78, 174, 245, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(78, 174, 245, 0.3)';
            }}
          >
            Commencer le Test
          </button>
        </main>
      </div>
    );
  }

  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const currentQ = questions[currentQuestionIndex];

  const handleBack = () => {
    if (currentQuestionIndex > 0 && canGoBack) {
      setCurrentQuestionIndex(prev => prev - 1);
      setCanGoBack(false); // Disable further back navigation until next answer
    }
  };

  // Safety check: if no current question, show loading
  if (!currentQ) {
    return (
      <div className="loading-screen">
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            border: '4px solid #E5E7EB', 
            borderTop: '4px solid #42a5f5',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <h2 style={{ color: '#42a5f5', fontWeight: '600' }}>Chargement...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <NavigationBar showDashboardButton={true} onViewDashboard={() => setViewMode('dashboard')} />
      
      <header style={{ marginTop: '80px' }}>
      
        <div className="progress-bar-container">
          <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
        </div>
      </header>

      <main className="quiz-card">
        <h2 className="question-text">Préférez-vous...</h2>
        
        <div className="options-container">
          <button 
            className="option-btn" 
            onClick={() => handleAnswer('A')}
          >
            {currentQ.textA}
          </button>
          
          <div className="divider">OU</div>
          
          <button 
            className="option-btn" 
            onClick={() => handleAnswer('B')}
          >
            {currentQ.textB}
          </button>
        </div>

        {currentQuestionIndex > 0 && (
          <button
            onClick={handleBack}
            disabled={!canGoBack}
            style={{
              background: 'transparent',
              color: canGoBack ? '#6B7280' : '#D1D5DB',
              border: `2px solid ${canGoBack ? '#E5E7EB' : '#F3F4F6'}`,
              padding: '12px 32px',
              fontSize: '14px',
              fontWeight: '600',
              borderRadius: '8px',
              cursor: canGoBack ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              marginTop: '32px',
              opacity: canGoBack ? 1 : 0.5
            }}
            onMouseEnter={(e) => {
              if (canGoBack) {
                e.target.style.borderColor = '#4EAEF5';
                e.target.style.color = '#4EAEF5';
              }
            }}
            onMouseLeave={(e) => {
              if (canGoBack) {
                e.target.style.borderColor = '#E5E7EB';
                e.target.style.color = '#6B7280';
              }
            }}
          >
            ← Question précédente
          </button>
        )}
      </main>
    </div>
  );
}

// --- RESULTS COMPONENT ---
function ResultsView({ results, resultId, message, onViewDashboard }) {
  const handleDownloadPDF = () => {
    if (resultId) {
      // Open PDF in new tab for download
      window.open(`${API_URL}/results/${resultId}/pdf`, '_blank');
    } else {
      alert('Result ID not available. Please try submitting the test again.');
    }
  };

  return (
    <>
      <NavigationBar showDashboardButton={true} onViewDashboard={onViewDashboard} />
      <div className="results-view-container" style={{ marginTop: '80px' }}>
        {/* Professional Header with gradient background */}
        <div className="results-view-header">
        <div className="success-indicator">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="results-view-title">Assessment Complete</h1>
        <p className="results-view-subtitle">Your personalized career analysis is ready</p>
        
        <div className="results-view-actions">
          <button onClick={onViewDashboard} className="btn-secondary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            View All Results Dashboard
          </button>
          <button onClick={handleDownloadPDF} className="btn-primary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download PDF
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="results-view-content">
        {/* Summary Box */}
        <div className="results-summary-box">
          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            Vos résultats indiquent que votre profil est plus orienté vers des métiers de <strong style={{ color: '#3B82F6' }}>{results.families[0]?.name}</strong>
            {results.families[1] && <span> et <strong style={{ color: '#3B82F6' }}>{results.families[1].name}</strong></span>}.
          </p>
          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '8px' }}>
            Votre profil personnel relève vos points forts :
          </p>
          <ul style={{ fontSize: '16px', lineHeight: '1.8', margin: 0, paddingLeft: '24px' }}>
            <li><strong style={{ color: '#3B82F6' }}>{results.subjobs[0]?.name}</strong></li>
            {results.subjobs[1] && <li><strong style={{ color: '#3B82F6' }}>{results.subjobs[1]?.name}</strong></li>}
            {results.subjobs[2] && <li><strong style={{ color: '#3B82F6' }}>{results.subjobs[2]?.name}</strong></li>}
          </ul>
        </div>

        {/* Career Families Section */}
        <div className="results-section">
          <div className="results-section-header">
            <div className="section-badge">1</div>
            <div>
              <h2>Familles de métiers recommandées pour vous</h2>
              <p className="section-description">Vos affinités par famille de métiers</p>
            </div>
          </div>
          <div className="results-section-content">
            {results.families.map((item, index) => (
              <div key={item.code} className="score-item-modern">
                <div className="score-item-header">
                  <span className="score-label-modern">{item.name}</span>
                  <span className="score-value-modern">{item.percentage}%</span>
                </div>
                <div className="score-bar-container-modern">
                  <div className="score-bar-center-line"></div>
                  <div 
                    className="score-bar-fill-modern score-bar-family" 
                    style={{
                      left: item.percentage >= 0 ? '50%' : `${50 + (item.percentage / 2)}%`,
                      width: `${Math.abs(item.percentage) / 2}%`
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Competencies Section */}
        <div className="results-section">
          <div className="results-section-header">
            <div className="section-badge">2</div>
            <div>
              <h2>Vos compétences fortes</h2>
              <p className="section-description">Détail de vos points forts par domaine de compétences</p>
            </div>
          </div>
          <div className="results-section-content">
            {results.subjobs.map((item, index) => (
              <div key={item.code} className="score-item-modern">
                <div className="score-item-header">
                  <span className="score-label-modern">{item.name}</span>
                  <span className="score-value-modern">{item.percentage}%</span>
                </div>
                <div className="score-bar-container-modern">
                  <div className="score-bar-center-line"></div>
                  <div 
                    className="score-bar-fill-modern score-bar-subjob" 
                    style={{
                      left: item.percentage >= 0 ? '50%' : `${50 + (item.percentage / 2)}%`,
                      width: `${Math.abs(item.percentage) / 2}%`
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommended Careers Section */}
        {results.recommended_sous_metiers && results.recommended_sous_metiers.length > 0 && (
          <div className="results-section recommendations-section-modern">
            <div className="results-section-header">
              <div className="recommendation-icon-badge">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2>Les sous-métiers recommandés pour vous</h2>
                <p className="section-description">Suggestions personnalisées basées sur votre profil</p>
              </div>
            </div>
            <div className="recommended-careers-grid">
              {results.recommended_sous_metiers.map((metier, index) => (
                <div key={index} className="career-card-modern">
                  <svg className="career-card-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{metier}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default App;